import React, { useState, useEffect, useMemo } from "react";
import { IoIosArrowRoundBack } from "react-icons/io"; // Import the back arrow icon
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "../Tables/Table.css";
import { GoDownload } from "react-icons/go";
import { AiOutlinePrinter } from "react-icons/ai"; // Import print icon
import { useRef } from "react";
import axiosInstance from "../axiosinstance";

function ViewLedger({ isSidebarExpanded }) {
  const [ledgerData, setLedgerData] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { client_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ledgerRef = useRef(null);
  const queryParams = new URLSearchParams(location.search);
  const fyStart = queryParams.get("fy_start") || "";
  const fyEnd = queryParams.get("fy_end") || "";
  const ledgerFyParams = useMemo(
    () => ({
      ...(fyStart ? { fy_start: fyStart } : {}),
      ...(fyEnd ? { fy_end: fyEnd } : {}),
    }),
    [fyStart, fyEnd]
  );
  // Check if client_id exists
  useEffect(() => {
    if (!client_id) {
      setError("Client ID is missing.");
      return;
    }
  }, [client_id]);

  function formatDate(dateString) {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      weekday: "short",
    };

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", options);
  }

  function formatPeriodDate(dateString) {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  // Fetch ledger data
  const fetchLedgerData = async () => {
    setLoading(true);
    setError(""); // Reset error message on each request
    try {
      const response = await axiosInstance.get(
        `/api/get_ledger_by_id/v2/${client_id}`,
        {
          params: ledgerFyParams,
        }
      );

      // Ensure data is available before setting state
      setLedgerData(response?.data?.result || []);
      setTotals(response?.data?.data?.[0] || {});
    } catch (err) {
      setError("Failed to fetch ledger data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch ledger data when component mounts or client_id changes
  useEffect(() => {
    fetchLedgerData();
  }, [client_id, location.search]);

  const clientCompanyName =
    ledgerData.length > 0 ? ledgerData[0].client_company_name : "";

  // Function to handle ledger download (Excel)
  const handleDownloadLedger = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/download_ledger/v2/${client_id}`,
        {
          params: ledgerFyParams,
          responseType: "blob", // This ensures the response is treated as a file (binary data)
        }
      );
      // Create a link to trigger the download (Excel file)
      const link = document.createElement("a");
      const fileName = `ledger_${client_id}.xlsx`; // Set file name with Excel extension
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // Excel MIME type
      });
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    } catch (error) {
      console.error("Error downloading ledger:", error);
      setError("Failed to download ledger.");
    }
  };

  const periodLabel =
    fyStart && fyEnd
      ? `Period: ${formatPeriodDate(fyStart)} - ${formatPeriodDate(fyEnd)}`
      : "";

  const handlePrint = () => {
    const printContent = ledgerRef.current;
    if (!printContent) return;

    const printWindow = window.open("", "", "width=800,height=600");
    if (!printWindow) return;

    const styles = Array.from(
      document.querySelectorAll("style, link[rel='stylesheet']")
    )
      .map((node) => node.outerHTML)
      .join("");

    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Ledger</title>
          <meta charset="utf-8" />
          ${styles}
          <style>
            @page { size: auto; margin: 12mm; }
            html, body {
              height: auto !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            body {
              background: #ffffff !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .view-ledger-page.table-container.expanded,
            .view-ledger-page.table-container.collapsed,
            .view-ledger-page {
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              height: auto !important;
              overflow: visible !important;
              background: #ffffff !important;
            }
            .view-ledger-page .view-ledger-content,
            .view-ledger-page .view-ledger-print-surface,
            .view-ledger-page .view-ledger-data-body,
            .view-ledger-page .view-ledger-scroll-body {
              height: auto !important;
              min-height: 0 !important;
              max-height: none !important;
              overflow: visible !important;
              box-shadow: none !important;
            }
            .view-ledger-page .view-ledger-content,
            .view-ledger-page .view-ledger-scroll-body {
              border-color: #e5e7eb !important;
            }
            .view-ledger-page .sticky-header,
            .view-ledger-page .view-ledger-footer {
              position: static !important;
              box-shadow: none !important;
            }
            .view-ledger-page .view-ledger-table {
              min-width: 100% !important;
              width: 100% !important;
            }
            .view-ledger-page .view-ledger-table thead {
              display: table-header-group !important;
            }
            .view-ledger-page .view-ledger-table tr,
            .view-ledger-page .view-ledger-footer,
            .view-ledger-page .view-ledger-footer .footer-content {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
          </style>
        </head>
        <body>
          ${printContent.outerHTML}
          <script>
            window.onload = function () {
              window.focus();
              window.print();
              setTimeout(function () { window.close(); }, 200);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  function formatNumbers(number) {
    if (number === null || number === undefined || isNaN(number)) return "N/A";

    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(number);
  }

  return (
    <div
      ref={ledgerRef}
      className={`table-container view-ledger-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content view-ledger-content">
        <div className="view-ledger-print-surface">
          <div className="table-header view-ledger-header">
            <div className="table-header-content">
              <div className="table-left-side view-ledger-title-group">
                {/* Back Button with arrow icon */}
                <div
                  onClick={() => navigate(-1)} // Navigate back to the previous page
                  className="back-button"
                >
                  <IoIosArrowRoundBack />
                </div>
                {/* Display client_company_name in the header */}
                <div className="view-ledger-heading">
                  <h2 className="ledger-title">
                    {clientCompanyName}{" "}
                    <span className="sub-title">(Detailed Ledger)</span>
                  </h2>
                  {periodLabel ? (
                    <p className="ledger-period-label">{periodLabel}</p>
                  ) : null}
                </div>
              </div>
              <div className="table-right-side view-ledger-actions">
                {/* Download button with icon */}
                <button
                  className="search-icon print-hidden"
                  onClick={handleDownloadLedger}
                  title="Download Ledger"
                >
                  <GoDownload size={20} color="gray" />
                </button>

                {/* Print button with icon */}
                <button
                  className="search-icon print-hidden"
                  onClick={handlePrint}
                  title="Print Ledger"
                >
                  <AiOutlinePrinter size={20} color="gray" />
                </button>
              </div>
            </div>
          </div>

          <div className="data-list-body view-ledger-data-body">
            <div className="data-list-scroll-body view-ledger-scroll-body">
              {loading ? (
                <p>Loading...</p>
              ) : error ? (
                <p className="error-message">{error}</p>
              ) : ledgerData.length > 0 ? (
                <table className="table multilevel-data view-ledger-table">
                  <thead className="sticky-header">
                    <tr className="table-row">
                      <th><span>SE-No</span></th>
                      <th>DATE</th>
                      <th className="amount-right">DEBIT</th>
                      <th className="amount-right">CREDIT</th>
                      <th>TRANSACTION TYPE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerData.map((ledger, index) => (
                      <tr className="hoverable-row" key={index}>
                        <td className="center-align ledger-table">
                          {index + 1}
                        </td>
                        <td className="center-align ledger-table">
                          {formatDate(ledger.date)}
                        </td>
                        <td className="center-align amount-right view-ledger-debit-cell">
                          {formatNumbers(ledger.debit)}
                        </td>
                        <td className="center-align amount-right view-ledger-credit-cell">
                          {formatNumbers(ledger.credit)}
                        </td>
                        <td className="center-align ledger-table">
                          {ledger.transaction_type}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No Ledger Records Found</p>
              )}

              <div className="footer-container view-ledger-footer">
                <div className="footer-content">
                  <span>
                    <strong>Total Credit: </strong>
                    {totals.total_credits || "0.00"}
                  </span>
                  <span>
                    <strong>Total Debit: </strong>
                    {totals.total_debits || "0.00"}
                  </span>
                  <span>
                    <strong>Total Outstanding: </strong>
                    {totals.total_outstanding || "0.00"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewLedger;
